import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Input,
  Empty,
  Alert,
  Skeleton,
  List,
  Tooltip,
  Switch,
  Divider,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  CloseOutlined,
  ReloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useAccess, MODULES } from "../../../hooks/useAccess";

const { Text, Paragraph } = Typography;

// Vendimet janë të njëjtat numra si enum-i AgentTaskDecision në backend.
const DECISION = { CONFIRM: 0, CORRECT: 1, REJECT: 2 };

const STATUS_OPTIONS = [
  { value: "Proposed", label: "Awaiting review" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "Corrected", label: "Corrected" },
  { value: "Rejected", label: "Rejected" },
];

const STATUS_TAG = {
  Proposed: "processing",
  Confirmed: "success",
  Corrected: "warning",
  Rejected: "error",
  AutoApproved: "success",
};

// Nën këtë besueshmëri fusha theksohet. Qëllimi është që rishikuesi të mos harxhojë kohë me
// fushat që modeli i di mirë — pikërisht ajo që e bën radhën të punueshme (§4 i strategjisë).
const LOW_CONFIDENCE = 0.8;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

/**
 * E rrafshon JSON-in në gjethe me të njëjtin format shtegu që përdor backend-i
 * (`invoiceNumber`, `items[2].price`) — kështu ajo që redakton njeriu këtu përputhet një-për-një
 * me rreshtat që dalin te AgentTaskReviews.
 */
const flattenJson = (value, prefix = "", out = []) => {
  if (value !== null && typeof value === "object") {
    if (Array.isArray(value)) {
      if (value.length === 0) out.push({ path: prefix || "$", value });
      else value.forEach((v, i) => flattenJson(v, `${prefix}[${i}]`, out));
      return out;
    }
    const keys = Object.keys(value);
    if (keys.length === 0) out.push({ path: prefix || "$", value });
    else keys.forEach((k) => flattenJson(value[k], prefix ? `${prefix}.${k}` : k, out));
    return out;
  }
  out.push({ path: prefix || "$", value });
  return out;
};

const parsePath = (path) =>
  path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter((s) => s.length > 0);

/** Vendos një gjethe ekzistuese. Nuk krijon kurrë çelësa të rinj — për strukturë ka modin raw. */
const setAtPath = (root, path, value) => {
  const segments = parsePath(path);
  if (segments.length === 0) return value;
  let node = root;
  for (let i = 0; i < segments.length - 1; i += 1) {
    if (node === null || typeof node !== "object") return root;
    node = node[segments[i]];
  }
  if (node !== null && typeof node === "object") {
    node[segments[segments.length - 1]] = value;
  }
  return root;
};

/**
 * Ruan tipin e propozimit. Një numër i korrigjuar mbetet numër, që diferenca në backend të mos
 * raportojë `100 -> "100"` si korrigjim fushe kur njeriu s'ndryshoi asgjë përveç formatimit.
 */
const coerceToOriginalType = (raw, original) => {
  if (typeof original === "number") {
    const n = Number(raw);
    return raw !== "" && Number.isFinite(n) ? n : raw;
  }
  if (typeof original === "boolean") return raw === "true" || raw === true;
  if (original === null) return raw === "" ? null : raw;
  return raw;
};

const displayValue = (value) => {
  if (value === null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ReviewTab = ({ agentId }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { can } = useAccess();
  const canReview = can(MODULES.AGENTS, "update");

  const [status, setStatus] = useState("Proposed");
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [edits, setEdits] = useState({});
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState("");
  const [rawError, setRawError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(null); // null | { reason }
  const [accuracy, setAccuracy] = useState(null);
  const [exemplars, setExemplars] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [adding, setAdding] = useState(null); // null | { inputSnippet, correctOutputJson, groupKey }
  const [addError, setAddError] = useState(null);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentTaskQueue", {
        method: "get",
        endpoint: "/v1/agent-tasks",
        params: { agentId, status, page: 1, pageSize: 50 },
      });
      setQueue(data?.data?.items || []);
      setTotal(data?.data?.totalCount || 0);
    } catch {
      setQueue([]);
      setTotal(0);
    } finally {
      setQueueLoading(false);
    }
  }, [agentId, status]);

  const loadAccuracy = useCallback(async () => {
    try {
      const { data } = await mutateRef.current("fetchAgentAccuracy", {
        method: "get",
        endpoint: `/v1/agent-tasks/agents/${agentId}/accuracy`,
      });
      setAccuracy(data?.data || null);
    } catch {
      setAccuracy(null);
    }
  }, [agentId]);

  const loadExemplars = useCallback(async () => {
    try {
      const { data } = await mutateRef.current("fetchAgentExemplars", {
        method: "get",
        endpoint: `/v1/agent-tasks/agents/${agentId}/exemplars`,
      });
      setExemplars(data?.data || []);
    } catch {
      setExemplars([]);
    }
  }, [agentId]);

  const toggleExemplar = useCallback(
    async (exemplar) => {
      setTogglingId(exemplar.exemplarId);
      try {
        const { error } = await mutateRef.current("toggleAgentExemplar", {
          method: "post",
          endpoint: `/v1/agent-tasks/exemplars/${exemplar.exemplarId}/active`,
          params: { isActive: !exemplar.isActive },
          alert: {
            success: exemplar.isActive
              ? "Example disabled — it will not enter any prompt again."
              : "Example re-enabled.",
          },
        });
        if (!error) await loadExemplars();
      } finally {
        setTogglingId(null);
      }
    },
    [loadExemplars]
  );

  const submitExemplar = useCallback(async () => {
    if (!adding?.inputSnippet?.trim()) {
      setAddError("The input snippet is required — it is the text that gets embedded.");
      return;
    }
    try {
      JSON.parse(adding.correctOutputJson);
    } catch (e) {
      setAddError(`The correct output must be valid JSON: ${e.message}`);
      return;
    }

    const { error } = await mutateRef.current("createAgentExemplar", {
      method: "post",
      endpoint: `/v1/agent-tasks/agents/${agentId}/exemplars`,
      payload: {
        inputSnippet: adding.inputSnippet.trim(),
        correctOutputJson: adding.correctOutputJson,
        groupKey: adding.groupKey?.trim() || null,
      },
      alert: { success: "Example added and embedded." },
    });
    if (error) return;
    setAdding(null);
    setAddError(null);
    await loadExemplars();
  }, [adding, agentId, loadExemplars]);

  const loadDetail = useCallback(async (taskId) => {
    setDetailLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentTask", {
        method: "get",
        endpoint: `/v1/agent-tasks/${taskId}`,
      });
      const task = data?.data || null;
      setDetail(task);
      setEdits({});
      setRawMode(false);
      setRawError(null);
      setRawJson(task?.outputJson ? JSON.stringify(JSON.parse(task.outputJson), null, 2) : "");
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    loadAccuracy();
    loadExemplars();
  }, [loadQueue, loadAccuracy, loadExemplars]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  // Propozimi i parsuar dhe gjethet e tij. Një dalje e prishur nuk e rrëzon tab-in — shfaqet
  // si gabim dhe rishikuesi mund ta refuzojë propozimin.
  const parsed = useMemo(() => {
    if (!detail?.outputJson) return { value: null, error: null };
    try {
      return { value: JSON.parse(detail.outputJson), error: null };
    } catch (e) {
      return { value: null, error: e.message };
    }
  }, [detail]);

  const confidence = useMemo(() => {
    if (!detail?.confidenceJson) return {};
    try {
      return JSON.parse(detail.confidenceJson) || {};
    } catch {
      return {};
    }
  }, [detail]);

  const leaves = useMemo(
    () => (parsed.value === null ? [] : flattenJson(parsed.value)),
    [parsed]
  );

  const confidenceFor = useCallback(
    (path) => {
      if (path in confidence) return confidence[path];
      const last = parsePath(path).pop();
      return last && last in confidence ? confidence[last] : undefined;
    },
    [confidence]
  );

  const changedPaths = useMemo(
    () =>
      Object.keys(edits).filter((path) => {
        const leaf = leaves.find((l) => l.path === path);
        if (!leaf) return false;
        return coerceToOriginalType(edits[path], leaf.value) !== leaf.value;
      }),
    [edits, leaves]
  );

  const buildFinalJson = useCallback(() => {
    if (rawMode) return rawJson;
    const clone = JSON.parse(detail.outputJson);
    changedPaths.forEach((path) => {
      const leaf = leaves.find((l) => l.path === path);
      setAtPath(clone, path, coerceToOriginalType(edits[path], leaf.value));
    });
    return JSON.stringify(clone);
  }, [rawMode, rawJson, detail, changedPaths, leaves, edits]);

  const hasChanges = rawMode
    ? rawJson.trim() !== "" &&
      detail?.outputJson &&
      (() => {
        try {
          return JSON.stringify(JSON.parse(rawJson)) !== JSON.stringify(JSON.parse(detail.outputJson));
        } catch {
          return false;
        }
      })()
    : changedPaths.length > 0;

  const submit = useCallback(
    async (decision, reviewNote) => {
      if (!detail) return;
      if (rawMode && decision === DECISION.CORRECT) {
        try {
          JSON.parse(rawJson);
        } catch (e) {
          setRawError(e.message);
          return;
        }
      }
      setSaving(true);
      try {
        const { error } = await mutateRef.current("reviewAgentTask", {
          method: "post",
          endpoint: `/v1/agent-tasks/${detail.taskId}/review`,
          payload: {
            decision,
            finalOutputJson: decision === DECISION.CORRECT ? buildFinalJson() : null,
            reviewNote: reviewNote || null,
          },
          alert: {
            success:
              decision === DECISION.REJECT
                ? "Proposal rejected."
                : decision === DECISION.CORRECT
                ? "Corrections saved — the agent now has a golden example."
                : "Proposal confirmed.",
          },
        });
        if (error) return;
        setSelectedId(null);
        setRejecting(null);
        await Promise.all([loadQueue(), loadAccuracy(), loadExemplars()]);
      } finally {
        setSaving(false);
      }
    },
    [detail, rawMode, rawJson, buildFinalJson, loadQueue, loadAccuracy, loadExemplars]
  );

  const renderAccuracy = () => (
    <Card size="small">
      <Row gutter={[16, 8]}>
        <Col xs={12} md={5}>
          <Statistic
            title="Measured accuracy"
            // null = s'ka ende rishikime. Një 0% do të lexohej si "gabon gjithmonë".
            value={accuracy?.accuracyPercent ?? "—"}
            suffix={accuracy?.accuracyPercent != null ? "%" : ""}
          />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="Awaiting review" value={accuracy?.pendingCount ?? 0} />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="Confirmed" value={accuracy?.confirmedCount ?? 0} />
        </Col>
        <Col xs={12} md={4}>
          <Statistic title="Corrected" value={accuracy?.correctedCount ?? 0} />
        </Col>
        <Col xs={24} md={7}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Most-corrected fields
          </Text>
          <div style={{ marginTop: 4 }}>
            {accuracy?.worstFields?.length ? (
              <Space wrap size={[4, 4]}>
                {accuracy.worstFields.slice(0, 4).map((f) => (
                  <Tag key={f.fieldPath} color="warning">
                    {f.fieldPath} ×{f.corrections}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );

  const renderQueue = () => (
    <Card
      size="small"
      title={`Queue (${total})`}
      extra={
        <Space size={4}>
          <Select
            size="small"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setSelectedId(null);
            }}
            options={STATUS_OPTIONS}
            style={{ width: 150 }}
          />
          <Tooltip title="Reload">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<ReloadOutlined />}
                  aria-label="Refresh review queue"
                  onClick={loadQueue}
                />
          </Tooltip>
        </Space>
      }
      styles={{ body: { padding: 0, maxHeight: 520, overflowY: "auto" } }}
    >
      {queueLoading ? (
        <div style={{ padding: 16 }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : queue.length === 0 ? (
        <Empty
          style={{ padding: 24 }}
          description={
            status === "Proposed" ? "Nothing waiting for review" : "No proposals with this status"
          }
        />
      ) : (
        <List
          size="small"
          dataSource={queue}
          renderItem={(t) => (
            <List.Item
              onClick={() => setSelectedId(t.taskId)}
              style={{
                cursor: "pointer",
                paddingInline: 12,
                background: t.taskId === selectedId ? "rgba(22,119,255,0.08)" : undefined,
              }}
            >
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Space wrap size={[6, 2]}>
                  <Text strong style={{ overflowWrap: "anywhere" }}>
                    {t.inputSummary || t.inputRef || `#${t.taskId}`}
                  </Text>
                  <Tag color={STATUS_TAG[t.status]}>{t.status}</Tag>
                  {t.correctedFieldCount > 0 && <Tag color="warning">{t.correctedFieldCount} fixed</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t.taskType}
                  {t.groupKey ? ` · ${t.groupKey}` : ""} · {formatDate(t.createdAt)}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  const renderFieldEditor = () => {
    if (parsed.error) {
      return (
        <Alert
          type="error"
          showIcon
          message="The proposal is not valid JSON"
          description={`${parsed.error} — it can only be rejected.`}
        />
      );
    }

    return (
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        {leaves.map((leaf) => {
          const conf = confidenceFor(leaf.path);
          const isLow = typeof conf === "number" && conf < LOW_CONFIDENCE;
          const changed = changedPaths.includes(leaf.path);
          const current = leaf.path in edits ? edits[leaf.path] : displayValue(leaf.value);

          return (
            <Row key={leaf.path} gutter={8} align="middle" wrap={false}>
              <Col flex="0 0 40%">
                <Space size={4} wrap>
                  <Text
                    style={{ fontSize: 12, overflowWrap: "anywhere" }}
                    type={isLow ? "warning" : undefined}
                    strong={isLow}
                  >
                    {leaf.path}
                  </Text>
                  {typeof conf === "number" && (
                    <Tag color={isLow ? "warning" : "default"} style={{ marginInlineEnd: 0 }}>
                      {Math.round(conf * 100)}%
                    </Tag>
                  )}
                </Space>
              </Col>
              <Col flex="auto">
                <Input
                  size="small"
                  value={current}
                  disabled={!canReview || detail.status !== "Proposed"}
                  status={changed ? "warning" : undefined}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, [leaf.path]: e.target.value }))
                  }
                />
              </Col>
            </Row>
          );
        })}
      </Space>
    );
  };

  const renderReviewed = () => (
    <>
      <Divider style={{ margin: "12px 0" }} />
      <Text strong>
        {detail.status} by {detail.reviewedByName || "—"} · {formatDate(detail.reviewedAt)}
      </Text>
      {detail.reviewNote && <Paragraph type="secondary">{detail.reviewNote}</Paragraph>}
      {detail.changes?.length > 0 && (
        <List
          size="small"
          header={<Text strong>{detail.changes.length} field(s) corrected</Text>}
          dataSource={detail.changes}
          renderItem={(c) => (
            <List.Item>
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <Text code style={{ fontSize: 12 }}>
                  {c.fieldPath}
                </Text>
                <Text style={{ fontSize: 12 }}>
                  <Text delete type="danger">
                    {c.oldValueJson ?? "(missing)"}
                  </Text>{" "}
                  →{" "}
                  <Text type="success">{c.newValueJson ?? "(removed)"}</Text>
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </>
  );

  const renderDetail = () => {
    if (!selectedId) {
      return (
        <Card size="small">
          <Empty description="Pick a proposal from the queue" />
        </Card>
      );
    }
    if (detailLoading || !detail) {
      return (
        <Card size="small">
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      );
    }

    const pending = detail.status === "Proposed";

    return (
      <Card
        size="small"
        title={
          <Space wrap size={[6, 2]}>
            <Text strong>{detail.inputSummary || `#${detail.taskId}`}</Text>
            <Tag color={STATUS_TAG[detail.status]}>{detail.status}</Tag>
          </Space>
        }
        extra={
          !parsed.error && (
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Raw JSON
              </Text>
              <Switch size="small" checked={rawMode} onChange={setRawMode} />
            </Space>
          )
        }
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
            {detail.taskType}
            {detail.groupKey ? ` · ${detail.groupKey}` : ""}
            {detail.inputRef ? ` · ${detail.inputRef}` : ""}
            {detail.model ? ` · ${detail.model}` : ""}
          </Text>

          {detail.duplicateOfTaskId && (
            <Alert
              type="info"
              showIcon
              message={`This exact input was already reviewed as #${detail.duplicateOfTaskId}.`}
            />
          )}

          {rawMode && !parsed.error ? (
            <>
              <Input.TextArea
                rows={16}
                value={rawJson}
                disabled={!canReview || !pending}
                onChange={(e) => {
                  setRawJson(e.target.value);
                  setRawError(null);
                }}
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
              {rawError && <Alert type="error" showIcon message={rawError} />}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Raw mode is for structural fixes — adding or removing fields. The field editor can
                only change values that already exist.
              </Text>
            </>
          ) : (
            renderFieldEditor()
          )}

          {pending && canReview && (
            <Space wrap>
              {/* Veprimi kryesor ndjek gjendjen: pa ndryshime konfirmimi është hapi i natyrshëm,
                  me ndryshime është ruajtja. E njëjta logjikë si te backend-i, ku statusi ndjek
                  atë që ndodhi e jo butonin që u shtyp. */}
              <Button
                variant={hasChanges ? "primary" : "secondary"}
                icon={<EditOutlined />}
                loading={saving}
                disabled={!hasChanges || !!parsed.error}
                onClick={() => submit(DECISION.CORRECT)}
              >
                Save corrections{changedPaths.length > 0 ? ` (${changedPaths.length})` : ""}
              </Button>
              <Button
                variant={hasChanges ? "secondary" : "primary"}
                icon={<CheckOutlined />}
                loading={saving}
                disabled={hasChanges || !!parsed.error}
                onClick={() => submit(DECISION.CONFIRM)}
              >
                Confirm as-is
              </Button>
              <Button
                variant="danger"
                icon={<CloseOutlined />}
                onClick={() => setRejecting({ reason: "" })}
              >
                Reject
              </Button>
            </Space>
          )}

          {pending && hasChanges && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Saving records one row per changed field — that is what the agent learns from.
            </Text>
          )}

          {!pending && renderReviewed()}
        </Space>
      </Card>
    );
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {renderAccuracy()}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          {renderQueue()}
        </Col>
        <Col xs={24} lg={15}>
          {renderDetail()}
        </Col>
      </Row>

      <Card
        size="small"
        title={`Golden examples (${exemplars.filter((e) => e.isActive).length} active)`}
        extra={
          canReview && (
            <Button
              variant="secondary"
              size="sm"
              icon={<PlusOutlined />}
              onClick={() => {
                setAdding({ inputSnippet: "", correctOutputJson: "", groupKey: "" });
                setAddError(null);
              }}
            >
              Add example
            </Button>
          )
        }
      >
        {exemplars.length === 0 ? (
          <Empty description="No examples yet — they are minted when a proposal is confirmed or corrected" />
        ) : (
          <List
            size="small"
            dataSource={exemplars}
            renderItem={(e) => (
              <List.Item
                actions={[
                  canReview ? (
                    <Button
                      key="toggle"
                      variant={e.isActive ? "danger" : "secondary"}
                      size="sm"
                      loading={togglingId === e.exemplarId}
                      onClick={() => toggleExemplar(e)}
                    >
                      {e.isActive ? "Disable" : "Enable"}
                    </Button>
                  ) : null,
                ]}
              >
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  <Space wrap size={[6, 2]}>
                    {!e.isActive && <Tag>disabled</Tag>}
                    {e.groupKey && <Tag color="blue">{e.groupKey}</Tag>}
                    <Tag>{e.source}</Tag>
                    {/* Pa vektor shembulli terhiqet vetem me GroupKey — ia vlen te dukej. */}
                    {!e.hasEmbedding && (
                      <Tooltip title="No embedding stored — retrieved by group key only">
                        <Tag color="warning">no vector</Tag>
                      </Tooltip>
                    )}
                    <Tag color={e.usageCount > 0 ? "blue" : "default"}>
                      {e.usageCount > 0 ? `used ${e.usageCount}×` : "never used"}
                    </Tag>
                  </Space>
                  <Text style={{ fontSize: 12, overflowWrap: "anywhere" }}>{e.inputSnippet}</Text>
                  <Text code style={{ fontSize: 11, overflowWrap: "anywhere" }}>
                    {e.correctOutputJson}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {e.taskId ? `from proposal #${e.taskId} · ` : ""}
                    {formatDate(e.createdAt)}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Modal
        open={!!adding}
        title="Add a golden example"
        confirmText="Add"
        onConfirm={submitExemplar}
        onClose={() => {
          setAdding(null);
          setAddError(null);
        }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Normally examples are minted automatically when a proposal is confirmed. Add one by
            hand to seed an agent before it goes live, or when the correct answer is already known.
          </Paragraph>

          <div>
            <Text strong>Input snippet</Text>
            {/* Ky tekst embed-ohet dhe krahasohet me pyetjet e reja — cilesia e terheqjes
                vendoset pikerisht ketu, jo te dalja. */}
            <Input.TextArea
              rows={3}
              placeholder="Faturë ABC SHPK, 3 artikuj, TVSH 18%"
              value={adding?.inputSnippet || ""}
              onChange={(e) => setAdding((p) => ({ ...p, inputSnippet: e.target.value }))}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              This is the text that gets embedded and compared against new inputs. Be specific —
              "invoice" matches everything and nothing.
            </Text>
          </div>

          <div>
            <Text strong>Correct output (JSON)</Text>
            <Input.TextArea
              rows={6}
              placeholder={'{"invoiceNumber":"F-13","total":100}'}
              value={adding?.correctOutputJson || ""}
              onChange={(e) => {
                setAdding((p) => ({ ...p, correctOutputJson: e.target.value }));
                setAddError(null);
              }}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          </div>

          <div>
            <Text strong>Group key (optional)</Text>
            <Input
              placeholder="furnitori:ABC-SHPK"
              value={adding?.groupKey || ""}
              onChange={(e) => setAdding((p) => ({ ...p, groupKey: e.target.value }))}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              An exact-match key beats similarity: it is retrieved first and costs no embedding.
            </Text>
          </div>

          {addError && <Alert type="error" showIcon message={addError} />}
        </Space>
      </Modal>

      <Modal
        open={!!rejecting}
        title="Reject proposal"
        confirmText="Reject"
        danger
        loading={saving}
        onConfirm={() => {
          const reason = rejecting?.reason?.trim();
          if (!reason) return;
          return submit(DECISION.REJECT, reason);
        }}
        onClose={() => setRejecting(null)}
      >
        <Paragraph type="secondary">
          Rejected proposals teach the agent nothing — the reason is kept so the pattern can be
          spotted later.
        </Paragraph>
        <Input.TextArea
          rows={3}
          placeholder="Why is this input unusable?"
          value={rejecting?.reason || ""}
          onChange={(e) => setRejecting({ reason: e.target.value })}
        />
      </Modal>
    </Space>
  );
};

export default ReviewTab;
